import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Link2, Linkedin, Loader2 } from "lucide-react";
import Reveal from "../components/Reveal";
import ArticleCard from "../components/blog/ArticleCard";
import { api, imageSized } from "../api";
import { cleanHtml, formatDate, useDocumentTitle } from "../lib/blog";
import { useToast } from "../hooks/use-toast";

export default function BlogPost() {
  const { slug } = useParams();
  const { toast } = useToast();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | missing

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    api.get(`/articles/${slug}`)
      .then((r) => { if (!cancelled) { setArticle(r.data); setState("ok"); } })
      .catch(() => !cancelled && setState("missing"));
    return () => { cancelled = true; };
  }, [slug]);

  useDocumentTitle(
    article ? `${article.seo_title || article.title} | Novocreste International` : null,
    article ? article.seo_description || article.excerpt : null
  );

  if (state === "loading") {
    return <div className="pt-48 pb-40 flex justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (state === "missing" || !article) {
    return (
      <section className="pt-44 pb-32 text-center px-5">
        <h1 className="font-display text-[36px] text-foreground">Article not found</h1>
        <p className="mt-3 text-muted-foreground">It may have been moved or unpublished.</p>
        <Link to="/blog" className="mt-8 inline-flex items-center gap-2 text-[#2ED9B8] font-medium"><ArrowLeft className="h-4 w-4" /> Back to Blog</Link>
      </section>
    );
  }

  const url = typeof window !== "undefined" ? window.location.href : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied" });
    } catch {
      toast({ title: "Couldn't copy the link", variant: "destructive" });
    }
  };

  return (
    <>
      <article className="pt-32 lg:pt-40 pb-16 lg:pb-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/blog" className="tap-target inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#2ED9B8] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <span className="mt-8 block w-fit text-[11px] uppercase tracking-[0.14em] text-[#2ED9B8] bg-[#0f2236] px-2.5 py-1 rounded-full">
            {article.category}
          </span>
          <h1 className="mt-5 font-display text-[34px] lg:text-[48px] leading-[1.1] text-foreground tracking-tight">{article.title}</h1>
          <p className="mt-5 text-sm text-muted-foreground">
            {article.author} · {formatDate(article.published_at)} · {article.read_minutes} min read
          </p>
        </div>

        {article.cover && (
          <div className="max-w-5xl mx-auto px-5 lg:px-8 mt-10">
            <img src={imageSized(article.cover, 1600)} alt="" className="w-full max-h-[480px] object-cover rounded-[24px] ring-1 ring-white/10" />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-5 lg:px-8 mt-10">
          <div className="article-body" dangerouslySetInnerHTML={{ __html: cleanHtml(article.body) }} />

          <div className="mt-12 pt-6 border-t border-white/10 flex items-center gap-3">
            <span className="text-sm text-muted-foreground mr-1">Share</span>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Share on LinkedIn"
              className="h-10 w-10 rounded-full border border-border bg-card hover:border-[#2ED9B8] flex items-center justify-center text-foreground transition-colors"
            >
              <Linkedin className="h-4 w-4" />
            </a>
            <button
              onClick={copy}
              aria-label="Copy link"
              className="h-10 w-10 rounded-full border border-border bg-card hover:border-[#2ED9B8] flex items-center justify-center text-foreground transition-colors"
            >
              <Link2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </article>

      {article.related?.length > 0 && (
        <section className="pb-20 lg:pb-28">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal><h2 className="font-display text-[28px] lg:text-[34px] text-foreground">More from the blog</h2></Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {article.related.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 60}><ArticleCard article={a} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
